import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { ImageModule } from 'primeng/image';
import { FileUploadModule } from 'primeng/fileupload';
import { MessageService } from 'primeng/api';
import { CoreApiService } from '../../../../core/service/core-api-service';
import { AppService } from '../../../../core/service/app.service';
import { JsonRequest } from '../../../../model/jsonRequest.model';
import { properties } from '../../../../../environments/properties';
import { ProductModel } from '../../../../model/product.model';
import { ProductCategoryModel } from '../../../../model/product-category.model';
import { compressImage } from '../../../../shared/utils/image-compression.util';
import { isAllowedUploadType } from '../../../../shared/utils/file-type-validator.util';

import { CentsCurrencyInputDirective } from '../../../../shared/directives/cents-currency-input.directive';

@Component({
  selector: 'app-edit-product',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    InputTextareaModule,
    InputNumberModule,
    DropdownModule,
    ToggleButtonModule,
    CardModule,
    ToastModule,
    ImageModule,
    FileUploadModule,
    CentsCurrencyInputDirective,
  ],
  providers: [MessageService],
  templateUrl: './edit-product.component.html',
  styleUrl: './edit-product.component.css',
})
export class EditProductComponent implements OnInit {
  storeId: number = 0;
  productId: number = 0;
  productInfo: ProductModel | null = null;
  ltsCategories: ProductCategoryModel[] = [];
  loading: boolean = false;
  selectedImage: File | null = null;
  imagePreview: string | null = null;

  formProduct = new FormGroup({
    pro_name: new FormControl('', Validators.required),
    pro_description: new FormControl(''),
    pro_price: new FormControl(0, Validators.required),
    pro_preparation_time_min: new FormControl(15),
    pro_is_featured: new FormControl(false),
    pro_category_id: new FormControl(null as number | null, Validators.required),
  });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly apiService: CoreApiService,
    private readonly util: AppService,
    private readonly messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.storeId = params['idstore'];
      this.productId = params['idproduct'];
      this.loadCategories();
      this.getProduct();
    });
  }

  loadCategories(): void {
    this.apiService.consumirWSGet(
      properties.serviceGetAllProductCategories,
      null,
      (data) => {
        this.ltsCategories = data.data ?? [];
      },
    );
  }

  getProduct(): void {
    const body: JsonRequest = {
      data: JSON.stringify({
        pro_id: this.productId,
      }),
      company: this.util.returnEmpresa(),
      fecha: new Date(),
    };
    this.loading = true;
    this.apiService.consumirWSPost(
      properties.serviceGetProductById,
      body,
      (data) => {
        this.productInfo = data.data;
        if (this.productInfo) {
          this.formProduct.setValue({
            pro_name: this.productInfo.pro_name,
            pro_description: this.productInfo.pro_description,
            pro_price: this.productInfo.pro_price,
            pro_preparation_time_min: this.productInfo.pro_preparation_time_min,
            pro_is_featured: this.productInfo.pro_is_featured,
            pro_category_id: this.productInfo.pro_category_id,
          });
        }
        this.loading = false;
      },
    );
  }

  save(): void {
    if (this.formProduct.invalid || !this.productInfo) {
      return;
    }

    const formData = new FormData();
    formData.append('pro_id', this.productInfo.pro_id.toString());
    formData.append('pro_name', this.formProduct.get('pro_name')?.value ?? '');
    formData.append(
      'pro_description',
      this.formProduct.get('pro_description')?.value ?? '',
    );
    formData.append(
      'pro_price',
      (this.formProduct.get('pro_price')?.value ?? 0).toString(),
    );
    formData.append(
      'pro_preparation_time_min',
      (this.formProduct.get('pro_preparation_time_min')?.value ?? 15).toString(),
    );
    formData.append(
      'pro_is_featured',
      (this.formProduct.get('pro_is_featured')?.value ?? false).toString(),
    );
    formData.append(
      'pro_category_id',
      (this.formProduct.get('pro_category_id')?.value ?? '').toString(),
    );
    formData.append('pro_store_id', this.storeId.toString());
    if (this.selectedImage) {
      formData.append('file', this.selectedImage, this.selectedImage.name);
    }

    this.loading = true;
    this.apiService.consumirWSFilePost(
      properties.serviceUpdateProduct,
      formData,
      (data) => {
        if (data.estado === properties.codeOK) {
          this.loading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Producto actualizado correctamente',
          });
          setTimeout(() => window.history.back(), 1000);
        } else {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar producto',
          });
        }
      },
    );
  }

  async onImageSelect(files: File[], fileUpload: any): Promise<void> {
    if (!isAllowedUploadType(files[0], false)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Formato no permitido. Solo se aceptan imágenes PNG o JPG.',
      });
      fileUpload.clear();
      return;
    }
    const file = await compressImage(files[0]);
    this.selectedImage = file;
    this.imagePreview = await this.previewFile(file);
    fileUpload.clear();
  }

  removeImage(): void {
    this.selectedImage = null;
    this.imagePreview = null;
  }

  previewFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (reader.result) {
          resolve(reader.result as string);
        } else {
          reject('Error al leer el archivo');
        }
      };
      reader.onerror = () => reject('Error al leer el archivo');
    });
  }

  cancel(): void {
    window.history.back();
  }
}
